/**
 * AccountingDocsSubTab.jsx — Muhasebe FAZ 3.6 / B10, "Belgeler" alt sekmesi.
 *
 * ÖNCEKİ HAL (yanlış): bu bileşenin tamamı bir "yakında" EmptyState'iydi ve
 * docstring'i "fatura/ihtarname/döküm PDF üretimi ve geçmişi henüz backend'de
 * yok" diyordu — bu ARTIK doğru değil, üç uç da mevcut ve öğrenci hub'ın
 * "Borç & Tahsilat" sekmesinde (DebtLedgerSubTab.jsx) zaten kullanılıyor.
 * B10 kapsamı KÜÇÜK tutuldu: buradaki tek iş mevcut PDF uçlarını "Belgeler"
 * sekmesinde TOPLAMAK — yeni bir API katmanı veya yeni backend uç YOK.
 *
 * Üç belge türü, üç ayrı kart:
 *   1. Ekstre (cari hesap dökümü) — useDownloadStatementPdf, her zaman tek
 *      buton (öğrenciye bağlı, ayrı bir liste gerektirmez).
 *   2. Faturalar — `detail.invoices` (StudentDetailPage'in zaten çektiği
 *      veri, AYRI bir fetch YOK) listelenir, her satır kendi
 *      useDownloadInvoicePdf çağrısını taşır.
 *   3. İcra dosyaları (ihtarname) — useLegalCasesForNotice ile mount'ta
 *      çekilir, her satır kendi useDownloadNoticePdf çağrısını taşır.
 *
 * Senet PDF ucu YOK (backend'de üretilmiyor) — burada uydurma bir "yakında"
 * satırı da eklenmedi, senetler zaten kendi sekmesinde (PromissoryNotesSubTab).
 *
 * i18n: YENİ ANAHTAR AÇILMADI. `accounting.docs.*` bloğu (LanguageContext.js)
 * DebtLedgerSubTab.jsx'in zaten kullandığı 21 anahtarla dolu — aynıları
 * burada da kullanılıyor. Genel boş/hata durumları için `accounting.empty.*`
 * / `accounting.error.*` (aynı dosyada, başka alt sekmelerin de kullandığı
 * paylaşılan anahtarlar) kullanıldı.
 *
 * ÖKSÜZ KALAN anahtarlar (SİLİNMEDİ — LanguageContext.js bu turun kapsamı
 * DIŞINDA, başka bir agent orada çalışıyordu): `accounting.docs.empty_title`,
 * `accounting.docs.empty_description`. Metni artık yanlış ("Belgeler
 * yakında" / "coming soon") olduğu için kasıtlı olarak KULLANILMADI.
 *
 * ETİKET DÜZELTMESİ (2026-09-29, feature/parasut-fatura-faz4-web): `detail.
 * invoices` koleksiyonundaki kayıt RESMİ FATURA DEĞİL — iç satış/borç kaydı
 * (resmi fatura Paraşüt'te kesiliyor). Bu yüzden `accounting.docs.invoice_pdf.*`
 * anahtarlarının GÖRÜNEN metni "Fatura PDF İndir" → "Satış Kaydı PDF İndir"
 * olarak değiştirildi — anahtar adları, `invoice_id`, API path'i ve DB alanları
 * DEĞİŞMEDİ. `InvoicePdfButton` (columnDefs.js) AYNI anahtarları kullandığı
 * için orada AYRICA bir metin değişikliği gerekmedi.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, ScrollText, Gavel, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import { EmptyState, LoadingState, StatusBadge } from '../../../components/admin';
import { fmtMoneyAccounting } from '../../../utils/money';
import {
  useDownloadStatementPdf,
  useDownloadInvoicePdf,
  useDownloadNoticePdf,
  useLegalCasesForNotice,
} from '../../../api/mutations/accountingDocuments';
import { ActionButton } from './noteUiKit';
import { fmtLedgerDate } from './columnDefs';

// İcra dosyası durumu -> StatusBadge varyantı. DebtLedgerSubTab.jsx'teki
// LEGAL_CASE_STATUS_VARIANT ile AYNI görsel eşleme, o dosyaya dokunulmadığı
// için burada da (üçüncü kez) tekrarlanıyor — AdminLegal.jsx'in kendi
// STATUS_COLORS'ı zaten ilk kopyaydı.
const LEGAL_CASE_STATUS_VARIANT = {
  pending: 'warning',
  notice_sent: 'info',
  legal_started: 'purple',
  payment_order: 'warning',
  seizure: 'danger',
  collected: 'success',
  closed: 'neutral',
};

function DocsCard({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl border-2 border-[var(--border-light)] bg-white p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} className="text-[var(--brand-primary)]" />
        <h3 className="font-display font-bold text-sm text-[var(--text-main)]">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// ── Ekstre (cari hesap dökümü) ──────────────────────────────────────────────
function StatementCard({ userId, t }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const downloadStatement = useDownloadStatementPdf();

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await downloadStatement.mutateAsync(userId);
      toast.success(t('accounting.docs.statement_pdf.success'));
    } catch {
      toast.error(t('accounting.docs.statement_pdf.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <DocsCard icon={ScrollText} title={t('accounting.docs.statement_pdf.label')}>
      <ActionButton
        variant="neutral"
        onClick={handleClick}
        loading={loading}
        icon={ScrollText}
        testId="docs-statement-download-btn"
        aria-label={t('accounting.docs.statement_pdf.aria_label')}
      >
        {loading ? t('accounting.docs.statement_pdf.loading') : t('accounting.docs.statement_pdf.label')}
      </ActionButton>
    </DocsCard>
  );
}

// ── Satış kaydı satırı ───────────────────────────────────────────────────────
function InvoiceRow({ invoice, t, lang }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const downloadInvoice = useDownloadInvoicePdf();

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await downloadInvoice.mutateAsync(invoice.invoice_id);
      toast.success(t('accounting.docs.invoice_pdf.success'));
    } catch {
      toast.error(t('accounting.docs.invoice_pdf.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex items-center justify-between gap-2 p-2.5 rounded-lg border-2 border-[var(--border-light)] text-xs"
      data-testid={`docs-invoice-row-${invoice.invoice_id}`}
    >
      <span className="flex flex-col gap-0.5">
        <span className="font-bold text-[var(--text-main)]">{invoice.invoice_no || invoice.invoice_id}</span>
        <span className="text-[var(--text-muted)]">
          {fmtMoneyAccounting(invoice.total_amount, lang)} · {fmtLedgerDate(invoice.issued_at, lang)}
        </span>
      </span>
      <ActionButton
        variant="neutral"
        onClick={handleClick}
        loading={loading}
        icon={FileText}
        testId={`docs-invoice-download-${invoice.invoice_id}`}
        aria-label={t('accounting.docs.invoice_pdf.aria_label').replace('{course}', invoice.invoice_no || invoice.invoice_id)}
      >
        {loading ? t('accounting.docs.invoice_pdf.loading') : t('accounting.docs.invoice_pdf.label')}
      </ActionButton>
    </div>
  );
}

// ── Satış kayıtları kartı ────────────────────────────────────────────────────
function InvoicesCard({ invoices, t, lang }) {
  return (
    <DocsCard icon={FileText} title={t('accounting.docs.invoice_pdf.label')}>
      {invoices.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)]" data-testid="docs-invoices-empty">
          {t('accounting.empty.description')}
        </p>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {invoices.map((inv) => (
            <InvoiceRow key={inv.invoice_id} invoice={inv} t={t} lang={lang} />
          ))}
        </div>
      )}
    </DocsCard>
  );
}

// ── İcra dosyası satırı (ihtarname) ─────────────────────────────────────────
function LegalCaseRow({ legalCase, t, lang }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const downloadNotice = useDownloadNoticePdf();

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await downloadNotice.mutateAsync(legalCase.case_id);
      toast.success(t('accounting.docs.notice_pdf.success'));
    } catch {
      toast.error(t('accounting.docs.notice_pdf.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex items-center justify-between gap-2 p-2.5 rounded-lg border-2 border-[var(--border-light)] text-xs"
      data-testid={`docs-legal-case-row-${legalCase.case_id}`}
    >
      <span className="flex flex-col gap-0.5">
        <span className="flex items-center gap-1.5">
          <span className="font-bold text-[var(--text-main)]">{legalCase.case_no || '—'}</span>
          <StatusBadge
            variant={LEGAL_CASE_STATUS_VARIANT[legalCase.status] || 'neutral'}
            label={t(`admin.legal.status.${legalCase.status}`)}
          />
        </span>
        <span className="text-[var(--text-muted)]">
          {fmtMoneyAccounting(legalCase.debt_amount, lang)} · {fmtLedgerDate(legalCase.opened_at || legalCase.created_at, lang)}
        </span>
      </span>
      <ActionButton
        variant="neutral"
        onClick={handleClick}
        loading={loading}
        icon={Gavel}
        testId={`docs-legal-case-download-${legalCase.case_id}`}
        aria-label={t('accounting.docs.notice_pdf.aria_label')}
      >
        {loading ? t('accounting.docs.notice_pdf.loading') : t('accounting.docs.notice_pdf.label')}
      </ActionButton>
    </div>
  );
}

// ── İcra dosyaları kartı ─────────────────────────────────────────────────────
function LegalCasesCard({ userId, t, lang }) {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const fetchLegalCases = useLegalCasesForNotice();

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(false);
    fetchLegalCases
      .mutateAsync(userId)
      .then((res) => {
        if (!cancelled) setCases(res || []);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return (
    <DocsCard icon={Gavel} title={t('accounting.docs.notice_pdf.label')}>
      {loading && <LoadingState variant="bars" count={2} />}
      {!loading && error && (
        <div className="flex items-center gap-2 text-xs text-rose-600" data-testid="docs-legal-cases-error">
          <AlertCircle size={14} />
          {t('accounting.docs.notice_pdf.error')}
        </div>
      )}
      {!loading && !error && cases.length === 0 && (
        <div className="text-xs" data-testid="docs-legal-cases-empty">
          <p className="font-bold text-[var(--text-main)]">{t('accounting.docs.notice_pdf.no_case_title')}</p>
          <p className="text-[var(--text-muted)] mt-0.5">{t('accounting.docs.notice_pdf.no_case_desc')}</p>
          <button
            type="button"
            onClick={() => navigate('/admin/legal')}
            className="mt-1.5 font-bold underline underline-offset-2 text-[var(--brand-primary)]"
            data-testid="docs-legal-cases-link"
          >
            {t('accounting.docs.notice_pdf.no_case_link')}
          </button>
        </div>
      )}
      {!loading && !error && cases.length > 0 && (
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {cases.map((lc) => (
            <LegalCaseRow key={lc.case_id} legalCase={lc} t={t} lang={lang} />
          ))}
        </div>
      )}
    </DocsCard>
  );
}

// ── Public ────────────────────────────────────────────────────────────────
export default function AccountingDocsSubTab({ userId, detail }) {
  const { t, lang } = useLanguage();
  const invoices = detail?.invoices || [];

  if (!userId) {
    return (
      <EmptyState
        icon={<AlertCircle size={40} strokeWidth={1.5} className="text-rose-500" />}
        title={t('accounting.error.title')}
        description={t('accounting.error.description')}
      />
    );
  }

  return (
    <div className="space-y-4">
      <StatementCard userId={userId} t={t} />
      <InvoicesCard invoices={invoices} t={t} lang={lang} />
      <LegalCasesCard userId={userId} t={t} lang={lang} />
    </div>
  );
}
